/**
 * API client for the Issue Tracker (backend: apps/oh_issues).
 *
 * Two audiences in one file: the PUBLIC per-centre form, which runs with no
 * login at all, and the Alfred board behind one.
 *
 * Every `apiEndpoints.X` reference lives INSIDE a function body (TDZ-safety —
 * mirrors safeguarding.js / parentOnboarding.js), and every call returns
 * `response.data`, the unwrapped body. The request helper resolves to the FULL
 * axios response; a wrapper that returned `response` hands the caller
 * `{data, status, …}` instead of the list it expected — the bug that made a
 * just-submitted incident report appear to vanish.
 */
import { sentryLogger } from 'sentry'

import { apiEndpoints } from './common'

import { makeRequestUrl } from '../app/helpers'
import { get, patch, post } from '../request'

/* ---- the public form. No login: the token in the URL is the centre. ---- */

const fetchPublicIssueForm = async (token) => {
  try {
    const response = await get(`${apiEndpoints.issues}/public/${token}`)
    return response.data
  } catch (error) {
    sentryLogger(error)
    throw error
  }
}

/**
 * Submits as multipart because the screenshots ride along with the answers —
 * the shared upload endpoint is behind auth, and this form has no login.
 * `payload.screenshots` is a FileList/array of File.
 */
const submitPublicIssue = async (token, payload) => {
  try {
    const form = new FormData()
    Object.entries(payload).forEach(([key, value]) => {
      if (key === 'screenshots' || value === undefined || value === null) return
      form.append(key, value)
    })
    Array.from(payload.screenshots || []).forEach((file) => {
      form.append('screenshots', file)
    })
    // 'Content-Type': undefined on purpose, and it must stay undefined.
    // request.js only defaults the header when the key is ABSENT
    // (`hasOwnProperty`), so declaring it undefined stops it forcing
    // application/json — and leaves axios to set multipart/form-data WITH the
    // boundary. Hardcoding 'multipart/form-data' here omits the boundary and
    // Django's MultiPartParser then rejects the body.
    const response = await post(
      `${apiEndpoints.issues}/public/${token}`,
      form,
      { headers: { 'Content-Type': undefined } }
    )
    return response.data
  } catch (error) {
    sentryLogger(error)
    throw error
  }
}

/* ---- the Alfred board ---- */

const fetchIssues = async (filters = {}) => {
  try {
    const response = await get(
      makeRequestUrl(`${apiEndpoints.issues}/alfred`, filters)
    )
    return response.data
  } catch (error) {
    sentryLogger(error)
    throw error
  }
}

/** One issue by uuid — the board opens it straight from a link (?issue=). */
const fetchIssue = async (uuid) => {
  try {
    const response = await get(`${apiEndpoints.issues}/${uuid}/alfred`)
    return response.data
  } catch (error) {
    sentryLogger(error)
    throw error
  }
}

const updateIssue = async (uuid, payload) => {
  try {
    const response = await patch(
      `${apiEndpoints.issues}/${uuid}/alfred`,
      payload
    )
    return response.data
  } catch (error) {
    sentryLogger(error)
    throw error
  }
}

/**
 * The owner's internal working notes on one issue (apps/oh_issues
 * models/note.py). Kept in Alfred only — never posted to Slack. Both calls
 * return `{ issue, reference, notes: [{uuid, text, author_name, created}] }`,
 * oldest first.
 */
const fetchIssueNotes = async (uuid) => {
  try {
    const response = await get(`${apiEndpoints.issues}/${uuid}/notes/alfred`)
    return response.data
  } catch (error) {
    sentryLogger(error)
    throw error
  }
}

/** Adds one note and resolves to the full, updated list. */
const addIssueNote = async (uuid, text) => {
  try {
    const response = await post(`${apiEndpoints.issues}/${uuid}/notes/alfred`, {
      text
    })
    return response.data
  } catch (error) {
    sentryLogger(error)
    throw error
  }
}

/**
 * "Connect my Alfred account" from Tim's Slack link: the logged-in person is
 * the Slack member inside the signed token. Errors are rethrown so the board
 * can show the server's own wording (expired / not valid).
 */
const connectTim = async (token) => {
  try {
    const response = await post(`${apiEndpoints.issues}/tim/connect/alfred`, {
      token
    })
    return response.data
  } catch (error) {
    sentryLogger(error)
    throw error
  }
}

/** `bucket` is the grain of the raised-vs-resolved series: day | week | month. */
const fetchIssuePulse = async (bucket) => {
  try {
    const response = await get(
      makeRequestUrl(`${apiEndpoints.issues}/pulse/alfred`, { bucket })
    )
    return response.data
  } catch (error) {
    sentryLogger(error)
    throw error
  }
}

const fetchIssuePendingReport = async () => {
  try {
    const response = await get(`${apiEndpoints.issues}/report/alfred`)
    return response.data
  } catch (error) {
    sentryLogger(error)
    throw error
  }
}

const fetchIssueCategories = async () => {
  try {
    const response = await get(`${apiEndpoints.issues}/categories/alfred`)
    return response.data
  } catch (error) {
    sentryLogger(error)
    throw error
  }
}

const updateIssueCategory = async (uuid, payload) => {
  try {
    const response = await patch(
      `${apiEndpoints.issues}/categories/${uuid}/alfred`,
      payload
    )
    return response.data
  } catch (error) {
    sentryLogger(error)
    throw error
  }
}

const fetchIssueFormLinks = async () => {
  try {
    const response = await get(`${apiEndpoints.issues}/form_links/alfred`)
    return response.data
  } catch (error) {
    sentryLogger(error)
    throw error
  }
}

export {
  addIssueNote,
  connectTim,
  fetchIssue,
  fetchIssueCategories,
  fetchIssueFormLinks,
  fetchIssueNotes,
  fetchIssuePendingReport,
  fetchIssuePulse,
  fetchIssues,
  fetchPublicIssueForm,
  submitPublicIssue,
  updateIssue,
  updateIssueCategory
}
